'use client'

/**
 * ChartNewsButton — a toolbar "News" button on the chart that reveals recent headlines on hover/click.
 *
 * Deliberately self-contained: it's a normal DOM dropdown that never touches the lightweight-charts
 * canvas/series, so it can't break the chart. Reuses StockNewsPanel (/api/news, Redis-cached 30 min),
 * which only fetches when the panel first opens. Opens on hover with a small close delay so moving the
 * cursor into the panel doesn't dismiss it; also toggles on click. Right-anchored + scrollable so it
 * never overflows the viewport.
 */

import { useEffect, useRef, useState } from 'react'
import { Newspaper } from 'lucide-react'
import StockNewsPanel from './StockNewsPanel'

// Same ET calendar day as now — so "today's news" matches the trading day, not the browser's local day.
function isTodayET(iso: string): boolean {
  if (!iso) return false
  const t = new Date(iso)
  if (Number.isNaN(t.getTime())) return false
  const fmt = (d: Date) => d.toLocaleDateString('en-US', { timeZone: 'America/New_York' })
  return fmt(t) === fmt(new Date())
}

export default function ChartNewsButton({ symbol }: { symbol: string }) {
  const [open, setOpen] = useState(false)
  const [loaded, setLoaded] = useState(false)   // only mount the panel (→ fetch) once opened
  const [todayCount, setTodayCount] = useState(0)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Lightweight fetch just to badge how many articles dropped TODAY — draws the eye when news breaks.
  useEffect(() => {
    if (!symbol) return
    let alive = true
    fetch(`/api/news?symbol=${encodeURIComponent(symbol)}`, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { articles?: { publishedUtc: string }[] } | null) => {
        if (!alive || !j?.articles) return
        setTodayCount(j.articles.filter((a) => isTodayET(a.publishedUtc)).length)
      })
      .catch(() => {})
    return () => { alive = false }
  }, [symbol])

  const openNow = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null }
    setLoaded(true)
    setOpen(true)
  }
  const closeSoon = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    closeTimer.current = setTimeout(() => setOpen(false), 220)
  }

  return (
    <div className="relative" onMouseEnter={openNow} onMouseLeave={closeSoon}>
      <button
        type="button"
        onClick={() => { setLoaded(true); setOpen((o) => !o) }}
        className={[
          'relative rounded-md px-3 py-1.5 text-xs font-semibold transition-all inline-flex items-center gap-2 border',
          open
            ? 'bg-amber-500 text-black border-amber-400 shadow-sm'
            : todayCount > 0
              ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-200 border-amber-300 dark:border-amber-500/40 hover:text-amber-700 dark:hover:text-white hover:border-amber-400'
              : 'bg-card/70 text-foreground/80 border-border hover:text-foreground dark:hover:border-zinc-600',
        ].join(' ')}
        aria-expanded={open}
        title={todayCount > 0 ? `${todayCount} news article${todayCount === 1 ? '' : 's'} today` : 'Recent headlines'}
      >
        <Newspaper className="w-3.5 h-3.5" />
        News
        {todayCount > 0 && (
          <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold leading-none text-white">{todayCount}</span>
        )}
        <svg className={['w-3 h-3 transition-transform', open ? 'rotate-180' : ''].join(' ')} viewBox="0 0 12 12" fill="none"><path d="M3 5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
        {todayCount > 0 && !open && (
          <span className="pointer-events-none absolute -right-1 -top-1 flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-rose-500" />
          </span>
        )}
      </button>

      {open && loaded && (
        <div
          className="absolute right-0 top-full z-50 mt-2 max-h-[70vh] w-[min(400px,90vw)] overflow-y-auto rounded-xl border border-border bg-background/95 p-3 shadow-2xl shadow-black/60 backdrop-blur"
          onMouseEnter={openNow}
          onMouseLeave={closeSoon}
        >
          <StockNewsPanel symbol={symbol} heading={`${symbol} News`} />
        </div>
      )}
    </div>
  )
}
